import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileCheck, CheckCircle, XCircle, AlertTriangle, X } from 'lucide-react';
import config from '../config/config';
import '../styles/DataChecklistView.css';

const DataChecklistView = ({ isOpen, onClose, documentType, noTiket }) => {
  const [checklist, setChecklist] = useState([]);
  const [checklistData, setChecklistData] = useState({});
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen && documentType && noTiket) {
      setIsClosing(false);
      fetchChecklistData();
    }
  }, [isOpen, documentType, noTiket]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  const fetchChecklistData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch checklist items
      const checklistResponse = await axios.get(
        `${config.API_URL}/api/checklist/${documentType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (checklistResponse.data.success) {
        setChecklist(checklistResponse.data.checklist);
        
        // Fetch saved checklist data
        try {
          const dataResponse = await axios.get(
            `${config.API_URL}/api/checklist/data/${noTiket}/${documentType}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (dataResponse.data.success) {
            setChecklistData(dataResponse.data.checklistData);
          }
        } catch (error) {
          // No saved data yet
          setChecklistData({});
        }
      }
    } catch (error) {
      console.error('Error fetching checklist:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProgressInfo = () => {
    const items = checklistData.items || {};
    const mandatoryItems = checklist.filter(item => item.priority === 'mandatory');
    const optionalItems = checklist.filter(item => item.priority === 'optional');
    
    const checkedMandatory = mandatoryItems.filter(item => items[item.id]?.checked === true).length;
    const checkedOptional = optionalItems.filter(item => items[item.id]?.checked === true).length;
    
    const totalMandatory = mandatoryItems.length;
    const totalOptional = optionalItems.length;
    
    const checkedCount = checkedMandatory + checkedOptional;
    const totalCount = totalMandatory + totalOptional;
    const percentage = totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0;
    const status = checklistData.status || (checkedMandatory === totalMandatory ? 'Sesuai' : 'Tidak Sesuai');
    
    return {
      checkedCount,
      totalCount,
      checkedMandatory,
      totalMandatory,
      checkedOptional,
      totalOptional,
      percentage,
      status,
      items
    };
  };

  if (!isOpen) return null;

  const progressInfo = getProgressInfo();

  return (
    <div className={`checklist-view-overlay ${isClosing ? 'closing' : ''}`}>
      <div className={`checklist-view-modal ${isClosing ? 'closing' : ''}`}>
        <div className="checklist-view-header">
          <div className="checklist-header-content">
            <FileCheck size={24} className="checklist-icon" />
            <div>
              <h3>Hasil Checklist Pengecekan</h3>
              <p>Dokumen: <strong>{documentType}</strong></p>
            </div>
          </div>
          <button onClick={handleClose} className="checklist-close-btn">
            <X size={20} />
          </button>
        </div>

        <div className="checklist-view-body">
          {loading ? (
            <div className="checklist-loading">
              <div className="loading-spinner"></div>
              <p>Memuat data checklist...</p>
            </div>
          ) : (
            <>
              <div className="checklist-status-summary">
                <div className="status-info">
                  <div className="status-badge">
                    <span className={`status-indicator ${progressInfo.status === 'Sesuai' ? 'status-sesuai' : 'status-tidak-sesuai'}`}>
                      {progressInfo.status === 'Sesuai' ? (
                        <CheckCircle size={16} />
                      ) : (
                        <XCircle size={16} />
                      )}
                      {progressInfo.status}
                    </span>
                  </div>
                  <div className="status-count">
                    <div className="count-detail">
                      <span className="mandatory-count">
                        Wajib: {progressInfo.checkedMandatory}/{progressInfo.totalMandatory}
                      </span>
                      <span className="optional-count">
                        Opsional: {progressInfo.checkedOptional}/{progressInfo.totalOptional}
                      </span>
                    </div>
                    <div className="total-count">
                      Total: {progressInfo.checkedCount} dari {progressInfo.totalCount} item
                    </div>
                  </div>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${progressInfo.percentage}%` }}
                  ></div>
                </div>
              </div>

              <div className="checklist-items">
                {checklist.length === 0 ? (
                  <div className="no-checklist">
                    <AlertTriangle size={24} />
                    <p>Belum ada checklist untuk dokumen ini</p>
                  </div>
                ) : Object.keys(progressInfo.items).length === 0 ? (
                  <div className="no-data">
                    <AlertTriangle size={24} />
                    <p>Belum ada data checklist yang tersimpan</p>
                  </div>
                ) : (
                  <>
                    {/* Mandatory Items Section */}
                    {checklist.filter(item => item.priority === 'mandatory').length > 0 && (
                      <>
                        <div className="checklist-section-header mandatory">
                          <span className="section-title">Item Wajib</span>
                          <span className="section-count">
                            {checklist.filter(item => item.priority === 'mandatory').length} item
                          </span>
                        </div>
                        {checklist
                          .filter(item => item.priority === 'mandatory')
                          .map((item) => (
                            <div key={item.id} className={`checklist-item-view ${progressInfo.items[item.id]?.checked ? 'checked' : 'unchecked'}`}>
                              <div className="checklist-status-icon">
                                {progressInfo.items[item.id]?.checked ? (
                                  <CheckCircle size={20} className="check-icon" />
                                ) : (
                                  <XCircle size={20} className="uncheck-icon" />
                                )}
                              </div>
                              <div className="checklist-content">
                                <div className="checklist-text-with-priority">
                                  <span className="checklist-text">{item.description}</span>
                                  <span className="priority-badge priority-mandatory">Wajib</span>
                                </div>
                                {progressInfo.items[item.id]?.userInput && (
                                  <div className="user-input-display">
                                    <div className="input-value-box">{progressInfo.items[item.id].userInput}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                      </>
                    )}

                    {/* Optional Items Section */}
                    {checklist.filter(item => item.priority === 'optional').length > 0 && (
                      <>
                        <div className="checklist-section-header optional">
                          <span className="section-title">Item Opsional</span>
                          <span className="section-count">
                            {checklist.filter(item => item.priority === 'optional').length} item
                          </span>
                        </div>
                        {checklist
                          .filter(item => item.priority === 'optional')
                          .map((item) => (
                            <div key={item.id} className={`checklist-item-view ${progressInfo.items[item.id]?.checked ? 'checked' : 'unchecked'}`}>
                              <div className="checklist-status-icon">
                                {progressInfo.items[item.id]?.checked ? (
                                  <CheckCircle size={20} className="check-icon" />
                                ) : (
                                  <XCircle size={20} className="uncheck-icon" />
                                )}
                              </div>
                              <div className="checklist-content">
                                <div className="checklist-text-with-priority">
                                  <span className="checklist-text">{item.description}</span>
                                  <span className="priority-badge priority-optional">Opsional</span>
                                </div>
                                {progressInfo.items[item.id]?.userInput && (
                                  <div className="user-input-display">
                                    <span className="input-label">Input User:</span>
                                    <div className="input-value-box">{progressInfo.items[item.id].userInput}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                      </>
                    )}
                  </>
                )}
              </div>

              {checklistData.updated_at && (
                <div className="checklist-timestamp">
                  <small>Terakhir diperbarui: {new Date(checklistData.updated_at).toLocaleString('id-ID')}</small>
                </div>
              )}
            </>
          )}
        </div>

        <div className="checklist-view-footer">
          <button onClick={onClose} className="btn-close">
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataChecklistView;